/**
 * UX Sprint 2 — Stage workspace shell.
 *
 * Premium wrapper rendered above the stage content area in normal UX
 * mode. Composes the hero card, readiness chips, an assistant hint,
 * and a slot for the stage's main content.
 *
 * Purely a UX composition layer. It does NOT mount any backend logic
 * or panel directly — it only renders a header band above whatever
 * panel `ProjectBoard` chooses to mount for the current `tab`.
 */
import { USER_STAGES, STAGE_META, userStageStatus } from "@/lib/constants";
import StageHeroCard from "./StageHeroCard";
import ReadinessChips from "./ReadinessChips";
import AssistantHint from "./AssistantHint";

export default function StageWorkspaceShell({
    userStageKey,
    project,
    onPrimaryAction,
    onOpenMoreTools,
    onOpenAssistant,
}) {
    const stage = USER_STAGES.find((s) => s.key === userStageKey);
    if (!stage || !project) return null;

    const meta = STAGE_META[userStageKey] || {};
    const stageIndex = USER_STAGES.findIndex((s) => s.key === userStageKey) + 1;
    const overall = userStageStatus(stage, project.stages);

    /* Per-sub-stage readiness chips — derived from existing
     * project.stages[*].status (zero new API calls). */
    const chips = (stage.statusFrom || []).map((backendKey) => {
        const raw = (project.stages?.[backendKey]?.status || "").toLowerCase();
        let chipStatus = "not_started";
        if (raw === "approved") chipStatus = "approved";
        else if (["processing", "running", "in_progress", "leased"].includes(raw)) chipStatus = "in_progress";
        else if (["pending", "completed", "manually_completed", "changes_requested", "rejected"].includes(raw)) chipStatus = "needs_review";
        else if (raw === "failed") chipStatus = "failed";
        return { label: backendKey.replaceAll("_", " "), status: chipStatus };
    });

    /* Assistant hint tone derived from overall stage state. */
    const hintTone =
        overall === "approved" ? "ready" :
        overall === "failed" ? "warning" :
        overall === "not_started" ? "missing" :
        "default";

    return (
        <div data-testid={`stage-workspace-${userStageKey}`} className="px-6 lg:px-10 py-6 space-y-5 bg-[#080808] border-b border-zinc-900">
            <StageHeroCard
                stageIndex={stageIndex}
                stageLabel={stage.label}
                purpose={meta.purpose}
                overallStatus={overall}
                primaryAction={
                    meta.primary && {
                        label: meta.primary.label,
                        onClick: () => onPrimaryAction && onPrimaryAction(meta.primary),
                        testid: "stage-hero-primary",
                    }
                }
                secondaryActions={[
                    onOpenAssistant && { label: "Ask Assistant", onClick: onOpenAssistant, testid: "stage-hero-ask-assistant" },
                    onOpenMoreTools && { label: "More Tools", onClick: onOpenMoreTools, testid: "stage-hero-more-tools" },
                ].filter(Boolean)}
            />

            <div className="flex flex-wrap items-center gap-3">
                {chips.length > 0 && <ReadinessChips items={chips} testidPrefix={`chip-${userStageKey}`} />}
                {meta.assistantHint && <AssistantHint text={meta.assistantHint} tone={hintTone} testid={`hint-${userStageKey}`} />}
            </div>
        </div>
    );
}
